import { LogManager } from '@src/utility/Logger';

const logger = LogManager.getLogger('editor:flushWindowToRenderer');

/**
 * Longest a seek or a Play will wait for a rebuilt window to reach the
 * renderer before going ahead without it.
 *
 * Comfortably over a normal flush (a debounce flush plus one iframe round
 * trip), so the bound is only reached when something has actually gone wrong.
 */
export const WINDOW_FLUSH_TIMEOUT_MS = 500;

/**
 * Push a rebuilt window to the renderer, but never block the caller for good.
 *
 * `VideoSourceStore.flushChanges()` resolves on the renderer's next state
 * change and carries **no timeout of its own** — a state change that never
 * arrives leaves its promise pending forever. Callers on the seek and play
 * paths await this before commanding the renderer, so an unbounded wait there
 * does not merely delay a frame: it wedges the transport, and the player stops
 * responding to seeks and drags entirely.
 *
 * Failing open is the right trade. The window rebuild is an optimisation on
 * correctness — without it the worst case is the black opening this guard
 * exists to prevent, which is strictly better than a dead transport.
 */
export async function flushWindowToRenderer(videoSourceStore: {
  flushChanges(): Promise<void>;
}): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const bound = new Promise<'timeout'>(resolve => {
    timer = setTimeout(() => resolve('timeout'), WINDOW_FLUSH_TIMEOUT_MS);
  });

  try {
    const outcome = await Promise.race([
      videoSourceStore.flushChanges().then(() => 'flushed' as const),
      bound,
    ]);
    if (outcome === 'timeout') {
      logger.warn(
        'Window flush did not complete in time; proceeding with the seek anyway',
        { timeoutMs: WINDOW_FLUSH_TIMEOUT_MS },
      );
    }
  } catch (error) {
    // A failed flush must not block playback either.
    logger.warn('Window flush failed; proceeding with the seek anyway', {
      error,
    });
  } finally {
    if (timer) clearTimeout(timer);
  }
}
